import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import { isEmpty } from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import CancellationsApi from '../../../api/CancellationsApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData } from '../../../context/auth';
import {
	CancellationData,
	withCancellationContext,
} from '../../../context/CancellationProvider';
import { ErrorData } from '../../../context/ErrorProvider';
import DropdownButton from '../../DropdownButton';
import { successToast } from '../../Toast';

interface Props {
	cancellationData: CancellationData;
	authData: AuthData;
	errorData: ErrorData;
}

const ReinstatementActions = (props: Props) => {
	const navigate = useNavigate();
	const { authData, cancellationData, errorData } = props;
	const { hasPermission } = authData;
	const {
		selectedCancellation,
		reinstatementUpdateObj,
		loadCancellation,
		setLoading,
		setReinstatementUpdateObj,
	} = cancellationData;
	const { reinstatement, policyLocator } = selectedCancellation;
	const { state } = reinstatement;
	const hiddenOptions: any = [];
	const isChanged = !isEmpty(reinstatementUpdateObj);

	const buttonList: any[] = [];
	if (state === 'draft') {
		const onUpdate = async () => {
			setLoading(true);
			try {
				await CancellationsApi.updateReinstatement(
					authData.auth.authorizationToken,
					reinstatement.locator,
					reinstatementUpdateObj,
				);
				successToast('Successfully updated reinstatement');
				await loadCancellation(selectedCancellation.locator, policyLocator);
				setReinstatementUpdateObj({});
			} catch (error) {
				errorData.handleErrorResponse(error, 'Failed to update reinstatement');
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
			}
			setLoading(false);
		};

		const onAccept = async () => {
			setLoading(true);
			try {
				await CancellationsApi.updateReinstatement(
					authData.auth.authorizationToken,
					reinstatement.locator,
					reinstatementUpdateObj,
				);
				await CancellationsApi.acceptReinstatement(
					authData.auth.authorizationToken,
					reinstatement.locator,
				);
				successToast('Successfully accepted reinstatement');
				await loadCancellation(selectedCancellation.locator, policyLocator);
				setReinstatementUpdateObj({});
			} catch (error) {
				errorData.handleErrorResponse(error, 'Failed to accept reinstatement');
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
			}
			setLoading(false);
		};

		hasPermission('reinstatement.update') &&
			buttonList.push(
				<Button
					key={0}
					color='primary'
					variant='contained'
					disabled={!isChanged}
					onClick={!isChanged ? null : onUpdate}
				>
					Save
				</Button>,
			);

		hasPermission('reinstatement.accept') &&
			hasPermission('reinstatement.update') &&
			buttonList.push(
				<Button key={1} color='primary' variant='contained' onClick={onAccept}>
					Accept
				</Button>,
			);
	}

	if (state === 'accepted') {
		const invalidate = async () => {
			setLoading(true);
			try {
				await CancellationsApi.invalidateReinstatement(
					authData.auth.authorizationToken,
					reinstatement.locator,
				);
				successToast('Successfully invalidated reinstatement');
				loadCancellation(selectedCancellation.locator, policyLocator);
				// go to details view
				navigate(
					`/policy/${selectedCancellation.policyLocator}/cancellations/${selectedCancellation.locator}/reinstatements/${selectedCancellation.reinstatement.locator}/details`,
				);
			} catch (error) {
				errorData.handleErrorResponse(
					error,
					'Failed to invalidate reinstatement',
				);
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
			}
			setLoading(false);
		};
		const issue = async () => {
			setLoading(true);
			try {
				await CancellationsApi.issueReinstatement(
					authData.auth.authorizationToken,
					reinstatement.locator,
				);
				successToast('Successfully issued reinstatement');
				loadCancellation(selectedCancellation.locator, policyLocator);
			} catch (error) {
				errorData.handleErrorResponse(error, 'Failed to issue reinstatement');
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
			}
			setLoading(false);
		};

		hasPermission('reinstatement.invalidate') &&
			buttonList.push(
				<Button
					key={0}
					color='primary'
					variant='contained'
					onClick={invalidate}
				>
					Invalidate
				</Button>,
			);

		hasPermission('reinstatement.issue') &&
			buttonList.push(
				<Button key={1} color='primary' variant='contained' onClick={issue}>
					Issue
				</Button>,
			);
	}

	const buttons = (
		<Stack direction='row' spacing={1}>
			{buttonList}
			{hiddenOptions.length ? <DropdownButton actions={hiddenOptions} /> : null}
		</Stack>
	);

	return <>{buttons}</>;
};

export default withCancellationContext<Props>(ReinstatementActions);
